import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  Task,
  TaskStatus,
  TaskPriority,
  User,
  Group,
  CreateTaskRequest,
  UpdateTaskRequest,
  AssignTaskRequest,
  ChangeStatusRequest,
  CreateGroupRequest,
  UpdateGroupRequest,
  TaskFilters,
  GroupFilters,
  UserFilters,
} from '../models/TaskModel';
import {
  getTasks,
  getTasksForOrder,
  getTask,
  createTask,
  updateTask,
  deleteTask,
  assignTask,
  changeTaskStatus,
  getMyTasks,
  getOverdueTasks,
  getTaskStatistics,
  getTaskStatuses,
  createTaskStatus,
  updateTaskStatus,
  deleteTaskStatus,
  getTaskPriorities,
  createTaskPriority,
  updateTaskPriority,
  deleteTaskPriority,
  getGroups,
  getGroup,
  createGroup,
  updateGroup,
  deleteGroup,
  getUsers,
  getUser,
} from './TaskCRUD';

// ==================== STATE INTERFACES ====================

interface MyTasksPaginationState {
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

interface TaskState {
  // Tasks
  tasks: Task[];
  allTasks: Task[];
  myTasks: Task[];
  myTasksCount: number;
  myTasksStatusCounts: Record<string, number>;
  myTasksPagination: MyTasksPaginationState | null;
  overdueTasks: Task[];
  currentTask: Task | null;
  loading: boolean;
  error: string | null;
  
  // Task Statuses & Priorities
  taskStatuses: TaskStatus[];
  taskPriorities: TaskPriority[];
  
  // Users & Groups
  users: User[];
  groups: Group[];
  
  // Statistics
  statistics: any;
  
  // UI State
  selectedTasks: string[];
  filters: TaskFilters;
}

interface GroupState {
  groups: Group[];
  currentGroup: Group | null;
  loading: boolean;
  error: string | null;
  filters: GroupFilters;
}

interface UserState {
  users: User[];
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  filters: UserFilters;
}

// ==================== INITIAL STATES ====================

const initialTaskState: TaskState = {
  tasks: [],
  allTasks: [],
  myTasks: [],
  myTasksCount: 0,
  myTasksStatusCounts: {},
  myTasksPagination: null,
  overdueTasks: [],
  currentTask: null,
  loading: false,
  error: null,
  taskStatuses: [],
  taskPriorities: [],
  users: [],
  groups: [],
  statistics: null,
  selectedTasks: [],
  filters: {},
};

const initialGroupState: GroupState = {
  groups: [],
  currentGroup: null,
  loading: false,
  error: null,
  filters: {},
};

const initialUserState: UserState = {
  users: [],
  currentUser: null,
  loading: false,
  error: null,
  filters: {},
};

// ==================== ASYNC THUNKS - TASKS ====================

export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (filters?: TaskFilters) => {
    const response = await getTasks(filters);
    return response;
  }
);

export const fetchTasksForOrder = createAsyncThunk(
  'tasks/fetchTasksForOrder',
  async ({ orderId, filters }: { orderId: string; filters?: TaskFilters }) => {
    const response = await getTasksForOrder(orderId, filters);
    return response;
  }
);

export const fetchTask = createAsyncThunk(
  'tasks/fetchTask',
  async (taskId: string) => {
    const response = await getTask(taskId);
    return response;
  }
);

export const createTaskAsync = createAsyncThunk(
  'tasks/createTask',
  async (taskData: CreateTaskRequest, { rejectWithValue }) => {
    try {
      const response = await createTask(taskData);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateTaskAsync = createAsyncThunk(
  'tasks/updateTask',
  async ({ id, taskData }: { id: string; taskData: UpdateTaskRequest }, { rejectWithValue }) => {
    try {
      const response = await updateTask(id, taskData);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteTaskAsync = createAsyncThunk(
  'tasks/deleteTask',
  async (taskId: string) => {
    await deleteTask(taskId);
    return taskId;
  }
);

export const assignTaskAsync = createAsyncThunk(
  'tasks/assignTask',
  async ({ taskId, assignData }: { taskId: string; assignData: AssignTaskRequest }) => {
    const response = await assignTask(taskId, assignData);
    return response;
  }
);

export const changeTaskStatusAsync = createAsyncThunk(
  'tasks/changeStatus',
  async ({ taskId, statusData }: { taskId: string; statusData: ChangeStatusRequest }) => {
    const response = await changeTaskStatus(taskId, statusData);
    return response;
  }
);

export const fetchMyTasks = createAsyncThunk(
  'tasks/fetchMyTasks',
  async (filters?: TaskFilters) => {
    const response = await getMyTasks(filters);
    return response;
  }
);

export const fetchOverdueTasks = createAsyncThunk(
  'tasks/fetchOverdueTasks',
  async (filters?: TaskFilters) => {
    const response = await getOverdueTasks(filters);
    return response;
  }
);

export const fetchTaskStatistics = createAsyncThunk(
  'tasks/fetchStatistics',
  async () => {
    const response = await getTaskStatistics();
    return response;
  }
);

// ==================== ASYNC THUNKS - TASK STATUSES ====================

export const fetchTaskStatuses = createAsyncThunk(
  'tasks/fetchTaskStatuses',
  async () => {
    const response = await getTaskStatuses();
    return response;
  }
);

export const createTaskStatusAsync = createAsyncThunk(
  'tasks/createTaskStatus',
  async (statusData: Partial<TaskStatus>) => {
    const response = await createTaskStatus(statusData);
    return response;
  }
);

export const updateTaskStatusAsync = createAsyncThunk(
  'tasks/updateTaskStatus',
  async ({ id, statusData }: { id: string; statusData: Partial<TaskStatus> }) => {
    const response = await updateTaskStatus(id, statusData);
    return response;
  }
);

export const deleteTaskStatusAsync = createAsyncThunk(
  'tasks/deleteTaskStatus',
  async (statusId: string) => {
    await deleteTaskStatus(statusId);
    return statusId;
  }
);

// ==================== ASYNC THUNKS - TASK PRIORITIES ====================

export const fetchTaskPriorities = createAsyncThunk(
  'tasks/fetchTaskPriorities',
  async () => {
    const response = await getTaskPriorities();
    return response;
  }
);

export const createTaskPriorityAsync = createAsyncThunk(
  'tasks/createTaskPriority',
  async (priorityData: Partial<TaskPriority>) => {
    const response = await createTaskPriority(priorityData);
    return response;
  }
);

export const updateTaskPriorityAsync = createAsyncThunk(
  'tasks/updateTaskPriority',
  async ({ id, priorityData }: { id: string; priorityData: Partial<TaskPriority> }) => {
    const response = await updateTaskPriority(id, priorityData);
    return response;
  }
);

export const deleteTaskPriorityAsync = createAsyncThunk(
  'tasks/deleteTaskPriority',
  async (priorityId: string) => {
    await deleteTaskPriority(priorityId);
    return priorityId;
  }
);

// ==================== ASYNC THUNKS - GROUPS ====================

export const fetchGroups = createAsyncThunk(
  'tasks/fetchGroups',
  async (filters?: GroupFilters) => {
    const response = await getGroups(filters);
    return response;
  }
);

export const fetchGroup = createAsyncThunk(
  'tasks/fetchGroup',
  async (groupId: string) => {
    const response = await getGroup(groupId);
    return response;
  }
);

export const createGroupAsync = createAsyncThunk(
  'tasks/createGroup',
  async (groupData: CreateGroupRequest) => {
    const response = await createGroup(groupData);
    return response;
  }
);

export const updateGroupAsync = createAsyncThunk(
  'tasks/updateGroup',
  async ({ id, groupData }: { id: string; groupData: UpdateGroupRequest }) => {
    const response = await updateGroup(id, groupData);
    return response;
  }
);

export const deleteGroupAsync = createAsyncThunk(
  'tasks/deleteGroup',
  async (groupId: string) => {
    await deleteGroup(groupId);
    return groupId;
  }
);

// ==================== ASYNC THUNKS - USERS ====================

export const fetchUsers = createAsyncThunk(
  'tasks/fetchUsers',
  async (filters?: UserFilters) => {
    const response = await getUsers(filters);
    return response;
  }
);

export const fetchUser = createAsyncThunk(
  'tasks/fetchUser',
  async (userId: string) => {
    const response = await getUser(userId);
    return response;
  }
);

// ==================== TASK SLICE ====================

const taskSlice = createSlice({
  name: 'tasks',
  initialState: initialTaskState,
  reducers: {
    clearTasks: (state) => {
      state.tasks = [];
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<TaskFilters>) => {
      state.filters = action.payload;
    },
    setSelectedTasks: (state, action: PayloadAction<string[]>) => {
      state.selectedTasks = action.payload;
    },
    toggleTaskSelection: (state, action: PayloadAction<string>) => {
      const taskId = action.payload;
      const index = state.selectedTasks.indexOf(taskId);
      if (index > -1) {
        state.selectedTasks.splice(index, 1);
      } else {
        state.selectedTasks.push(taskId);
      }
    },
    clearSelectedTasks: (state) => {
      state.selectedTasks = [];
    },
    setCurrentTask: (state, action: PayloadAction<Task | null>) => {
      state.currentTask = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.results;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tasks';
      })
      // Fetch tasks for order
      .addCase(fetchTasksForOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasksForOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.results;
      })
      .addCase(fetchTasksForOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tasks for order';
      })
      // Create task
      .addCase(createTaskAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTaskAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks.push(action.payload);
      })
      .addCase(createTaskAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create task';
      })
      // Update task
      .addCase(updateTaskAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTaskAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      .addCase(updateTaskAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update task';
      })
      // Delete task
      .addCase(deleteTaskAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTaskAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.filter(task => task.id !== action.payload);
        state.selectedTasks = state.selectedTasks.filter(id => id !== action.payload);
      })
      .addCase(deleteTaskAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete task';
      })
      // Fetch my tasks
      .addCase(fetchMyTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.myTasks = action.payload.results || [];
        state.myTasksCount = action.payload.count ?? 0;
        state.myTasksStatusCounts = action.payload.status_counts ?? {};
        state.myTasksPagination = {
          page: action.payload.page ?? 1,
          page_size: action.payload.page_size ?? 0,
          total_pages: action.payload.total_pages ?? 1,
          has_next: Boolean(action.payload.has_next),
          has_previous: Boolean(action.payload.has_previous),
        };
      })
      .addCase(fetchMyTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch my tasks';
      })
      // Fetch task statuses
      .addCase(fetchTaskStatuses.fulfilled, (state, action) => {
        state.taskStatuses = action.payload.results;
      })
      // Create task status
      .addCase(createTaskStatusAsync.fulfilled, (state, action) => {
        state.taskStatuses.push(action.payload);
      })
      // Fetch task priorities
      .addCase(fetchTaskPriorities.fulfilled, (state, action) => {
        state.taskPriorities = action.payload.results;
      })
      // Create task priority
      .addCase(createTaskPriorityAsync.fulfilled, (state, action) => {
        state.taskPriorities.push(action.payload);
      })
      // Fetch users
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users = action.payload.results;
      })
      // Fetch groups
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.groups = action.payload.results;
      })
      // Create group
      .addCase(createGroupAsync.fulfilled, (state, action) => {
        state.groups.push(action.payload);
      });
  },
});

// ==================== GROUP SLICE ====================

const groupSlice = createSlice({
  name: 'groups',
  initialState: initialGroupState,
  reducers: {
    clearGroups: (state) => {
      state.groups = [];
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<GroupFilters>) => {
      state.filters = action.payload;
    },
    setCurrentGroup: (state, action: PayloadAction<Group | null>) => {
      state.currentGroup = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.results;
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch groups';
      })
      .addCase(createGroupAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGroupAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.groups.push(action.payload);
      })
      .addCase(createGroupAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create group';
      });
  },
});

// ==================== USER SLICE ====================

const userSlice = createSlice({
  name: 'users',
  initialState: initialUserState,
  reducers: {
    clearUsers: (state) => {
      state.users = [];
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<UserFilters>) => {
      state.filters = action.payload;
    },
    setCurrentUser: (state, action: PayloadAction<User | null>) => {
      state.currentUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.results;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch users';
      });
  },
});

// ==================== EXPORTS ====================

export const {
  clearTasks,
  clearError,
  setFilters,
  setSelectedTasks,
  toggleTaskSelection,
  clearSelectedTasks,
  setCurrentTask,
} = taskSlice.actions;

export const {
  clearGroups,
  clearError: clearGroupError,
  setFilters: setGroupFilters,
  setCurrentGroup,
} = groupSlice.actions;

export const {
  clearUsers,
  clearError: clearUserError,
  setFilters: setUserFilters,
  setCurrentUser,
} = userSlice.actions;

export const taskReducer = taskSlice.reducer;
export const groupReducer = groupSlice.reducer;
export const userReducer = userSlice.reducer;
